import { Box, Link, SpaceBetween } from '@cloudscape-design/components';
import type { PlaceBasic, PlaceReportCompetitor } from '../common/types';
import { GoogleIcon } from '../common/icons/GoogleIcon';
import { SourceRatingRow } from './SourceRatingRow';

export type CompetitorSectionProps = {
  competitors: PlaceReportCompetitor[];
  place: PlaceBasic;
};

function formatDelta(value: number, suffix: string, locale = false): string {
  const sign = value > 0 ? '+' : '';
  const formatted = locale ? value.toLocaleString() : String(value);
  return `${sign}${formatted}${suffix}`;
}

export function CompetitorSection({ competitors, place }: CompetitorSectionProps) {
  if (!competitors || competitors.length === 0) {
    return null;
  }

  return (
    <div className="pt-3">
      <Box variant="h3" padding={{ bottom: 'xxs' }}>
        Local Competitors
      </Box>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {competitors.map((competitor) => (
          <CompetitorCard
            key={competitor.id}
            competitor={competitor}
            subjectRating={place.rating ?? 0}
            subjectReviewCount={place.total_review_count ?? 0}
          />
        ))}
      </div>
    </div>
  );
}

type CompetitorCardProps = {
  competitor: PlaceReportCompetitor;
  subjectRating: number;
  subjectReviewCount: number;
};

function CompetitorCard({ competitor, subjectRating, subjectReviewCount }: CompetitorCardProps) {
  const ratingDelta = (competitor.rating ?? 0) - subjectRating;
  const reviewDelta = (competitor.total_review_count ?? 0) - subjectReviewCount;

  return (
    <div className="border border-gray-200 rounded-lg p-3">
      <SpaceBetween direction="vertical" size="xxs">
        <div>
          <Box fontSize="body-s" fontWeight="bold">
            {competitor.google_maps_url ? (
              <Link href={competitor.google_maps_url} target="_blank" fontSize="body-s">
                {competitor.name}
              </Link>
            ) : (
              competitor.name
            )}
          </Box>
          {competitor.distance_miles != null && (
            <Box variant="small" color="text-body-secondary" fontSize="body-s">
              {competitor.distance_miles.toFixed(1)} mi away
            </Box>
          )}
        </div>

        <SourceRatingRow
          icon={<GoogleIcon />}
          name="Google"
          rating={competitor.rating ?? undefined}
          reviewCount={competitor.total_review_count ?? undefined}
          link={competitor.google_maps_url ?? undefined}
          stacked
        />

        <div className="flex flex-wrap gap-1.5">
          {ratingDelta > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
              {formatDelta(parseFloat(ratingDelta.toFixed(1)), ' stars')}
            </span>
          )}
          {reviewDelta > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
              {formatDelta(reviewDelta, ' reviews', true)}
            </span>
          )}
        </div>
      </SpaceBetween>
    </div>
  );
}
